import { Controller } from 'react-hook-form';
import React, { useEffect, useRef, useState } from 'react';
import Input from 'src/components/Input/Input';
import { clsx } from 'clsx';

export const ToneOfVoiceStyleDescriptionField = ({
  field,
  data,
  control,
  readonly,
  style,
  setValue,
  watch,
  compact,
}) => {
  const [isExpandNeeded, setExpandNeeded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef(null);
  const fieldValue = watch(field.key);

  useEffect(() => {
    if (readonly && data) {
      setValue(field.key, data);
      return;
    }
    if (fieldValue) {
      setValue(field.key, fieldValue);
      return;
    }
    if (data) {
      setValue(field.key, data);
    }
  }, [readonly, data]);

  useEffect(() => {
    if (style === 'custom') {
      setValue(field.key, fieldValue || data || '');
    } else {
      setValue(field.key, '');
    }
  }, [style]);

  useEffect(() => {
    if (wrapperRef.current) {
      const isNeeded = wrapperRef.current.clientHeight < wrapperRef.current.firstChild.offsetHeight;

      setExpandNeeded(isNeeded);
    }
  }, [wrapperRef.current]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return Boolean(field) ? (
    <div className={'pl-2.5 border-l border-[#a498ff80]'}>
      {Boolean(readonly && data) && (
        <div className={'relative'}>
          <div
            ref={wrapperRef}
            className={clsx(
              'overflow-hidden transition-all duration-500 ease-in-out',
              expanded ? 'max-h-auto' : 'max-h-36',
            )}
          >
            <div className={'h-[calc(100%-40px)]'}>
              <p className={'font-medium text-sm text-ebony_opacity_50 whitespace-break-spaces'}>
                {data}
              </p>
            </div>
          </div>
          {(expanded || isExpandNeeded) && (
            <div
              onClick={toggleExpanded}
              className={'text-[#7A6BE5] mt-2 font-semibold text-sm hover:underline cursor-pointer'}
            >
              {expanded ? 'Show Less...' : 'Show More...'}
            </div>
          )}
        </div>
      )}
      {Boolean(style === 'casual') && (
        <p className={'font-medium text-sm text-ebony_opacity_50'}>
          Friendly, conversational, and approachable. This tone makes interactions feel more
          personal and engaging. Perfect for informal customer support and sales conversations where
          a warm, human touch is preferred.
        </p>
      )}
      {Boolean(style === 'professional') && (
        <p className={'font-medium text-sm text-ebony_opacity_50'}>
          Clear, precise, and business-like. This tone ensures responses are formal, structured, and
          authoritative, making it ideal for corporate environments, technical support, and B2B
          interactions where professionalism is key.
        </p>
      )}
      {Boolean(!readonly && style === 'custom') && (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className={'flex flex-col w-full'}>
              <Input
                readonly={readonly}
                textareaRows={compact ? 5 : 3}
                type={'textarea'}
                label={'Description*'}
                placeholder={
                  'Provide your communication style description here. For example: Balance between professionalism and a personable tone. Your responses should be engaging but not overly humorous, making the client feel heard and valued.'
                }
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
              />
            </div>
          )}
        />
      )}
    </div>
  ) : null;
};
